// Country dial codes for the phone Input dropdown. India first (default),
// then the rest alphabetically by name. `dial` includes the leading '+'.
// Used by <Input variant="phone"> to compose/parse E.164 values.

import { COUNTRIES } from '@bbpos/types';
import type { Country } from '@bbpos/types';

export type PhoneCountry = Country;

export const DEFAULT_DIAL = '+91';

// The list lives in @bbpos/types so supplier forms and the API share it.
export const PHONE_COUNTRIES: readonly PhoneCountry[] = COUNTRIES;

// Dial codes sorted longest-first, for greedy prefix matching when parsing an
// existing E.164 value back into (dial, national).
const DIALS_BY_LENGTH = [...new Set(PHONE_COUNTRIES.map((c) => c.dial))].sort(
  (a, b) => b.length - a.length,
);

/**
 * Split an E.164 string into its dial code and national digits. `fallbackDial`
 * is used when the value carries no recognizable dial code of its own (empty or
 * bare national digits) — callers that know the tenant's country pass its dial
 * so the picker does not silently assume India.
 */
export function splitE164(
  value: string | undefined | null,
  fallbackDial: string = DEFAULT_DIAL,
): { dial: string; national: string } {
  const v = (value ?? '').trim();
  if (v.startsWith('+')) {
    const match = DIALS_BY_LENGTH.find((d) => v.startsWith(d));
    if (match) return { dial: match, national: v.slice(match.length).replace(/\D/g, '') };
  }
  // No recognizable dial code — treat the whole thing as national digits.
  return { dial: fallbackDial, national: v.replace(/\D/g, '') };
}

// Digits after the dial code for the countries we sell in (mobile numbers).
// A country not listed here is only held to the generic E.164 shape.
export const PHONE_NATIONAL_LENGTHS: Readonly<Record<string, number>> = {
  '+91': 10,
  '+968': 8,
  '+971': 9,
  '+966': 9,
  '+973': 8,
  '+974': 8,
  '+965': 8,
};

const PHONE_EXAMPLES: Readonly<Record<string, string>> = {
  '+91': '9876543210',
  '+968': '91234567',
  '+971': '501234567',
  '+966': '512345678',
  '+973': '36001234',
  '+974': '33123456',
  '+965': '50012345',
};

/** A sample national number for the placeholder of the selected dial code. */
export function phonePlaceholder(dial: string): string | undefined {
  return PHONE_EXAMPLES[dial];
}

/**
 * Why an E.164 value has the wrong number of digits for its country, or null
 * when its length is right (or the country has no rule).
 */
export function phoneLengthError(value: string | undefined | null): string | null {
  const v = (value ?? '').trim();
  if (!v.startsWith('+')) return null;
  const { dial, national } = splitE164(v);
  const length = PHONE_NATIONAL_LENGTHS[dial];
  if (!length || national.length === length) return null;
  const name = PHONE_COUNTRIES.find((c) => c.dial === dial)?.name ?? dial;
  return `Enter ${length === 8 ? 'an' : 'a'} ${length}-digit ${name} mobile number`;
}
